import React from 'react';
import { Link } from 'react-router-dom';
import {
  Car,
  FileText,
  Users,
  Shield,
  BarChart3,
  LogOut,
  Settings
} from 'lucide-react';
import { useAuth } from 'react-oidc-context';

const Hub: React.FC = () => {
  const auth = useAuth();
  const hasAuth = Boolean(auth && typeof auth.isAuthenticated !== 'undefined');
  const isAuthenticated = hasAuth && auth!.isAuthenticated;
  const handleLogin = () => {
    if (!hasAuth) {
      alert('Login nicht verfügbar: OIDC ist nicht konfiguriert.');
      return;
    }
    auth!.signinRedirect().catch((err: unknown) => {
      // eslint-disable-next-line no-console
      console.error('OIDC signinRedirect failed:', err);
      alert('Login konnte nicht gestartet werden. Bitte OIDC-Konfiguration prüfen.');
    });
  };
  const handleLogout = () => {
    if (!hasAuth) return;
    auth!.signoutRedirect().catch((err: unknown) => {
      // eslint-disable-next-line no-console
      console.error('OIDC signoutRedirect failed:', err);
      alert('Logout konnte nicht gestartet werden.');
    });
  };

  // Quick dev helper: if no OIDC, show a minimal token setter to avoid restarts
  const [tempToken, setTempToken] = React.useState('');
  // BFF setzt den Token serverseitig. Banner zur Token-Eingabe ist nicht mehr nötig.
  const showBaserowTokenBanner = false;
  const saveToken = () => {
    try {
      localStorage.setItem('REACT_APP_BASEROW_TOKEN', tempToken.trim());
      alert('Token gespeichert. Bitte Seite neu laden.');
    } catch {}
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Fuhrpark Management</h1>
              <p className="text-gray-600 mt-1">Zentrale Verwaltung und Übersicht</p>
            </div>
            <div className="flex items-center space-x-4">
              {hasAuth && isAuthenticated ? (
                <>
                  <div className="text-right">
                    <p className="text-sm text-gray-500 dark:text-gray-300">Willkommen</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{auth!.user?.profile?.name || auth!.user?.profile?.preferred_username || 'Nutzer'}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </>
              ) : hasAuth ? (
                <button
                  onClick={handleLogin}
                  className="flex items-center space-x-2 px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
                >
                  <span>Login</span>
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {showBaserowTokenBanner && (
          <div className="mb-6 p-4 border border-yellow-300 bg-yellow-50 rounded-md">
            <div className="font-medium mb-2">Baserow Token nicht geladen</div>
            <div className="text-sm mb-3">Geben Sie den Token ein und speichern Sie ihn lokal (nur für Entwicklung), oder legen Sie REACT_APP_BASEROW_TOKEN in der .env an und starten Sie neu.</div>
            <div className="flex items-center gap-2">
              <input value={tempToken} onChange={(e) => setTempToken(e.target.value)} placeholder="Token" className="border px-2 py-1 rounded w-full" />
              <button onClick={saveToken} className="px-3 py-1 border rounded">Speichern</button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Dashboard */}
          <Link
            to="/dashboard"
            className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-200 hover:border-[#add015]"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-[#add015] bg-opacity-20 group-hover:bg-[#add015] transition-colors">
                <BarChart3 className="w-8 h-8 text-[#add015] group-hover:text-white" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#add015] transition-colors">
              Dashboard
            </h3>
            <p className="text-gray-600">
              Übersicht aller Fahrzeuge, Statistiken und Verwaltungsfunktionen
            </p>
          </Link>

          {/* Schadenmanagement */}
          <Link
            to="/fahrzeugmanagement-schaeden"
            className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-200 hover:border-[#add015]"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-[#add015] bg-opacity-20 group-hover:bg-[#add015] transition-colors">
                <Shield className="w-8 h-8 text-[#add015] group-hover:text-white" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#add015] transition-colors">
              Fahrzeugmanagement&Schäden
            </h3>
            <p className="text-gray-600">
              Vorgänge, Termine und Schäden rund ums Fahrzeug verwalten
            </p>
          </Link>

          {/* Dokumentenablage */}
          <Link
            to="/dokumentenablage"
            className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-200 hover:border-[#add015]"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-[#add015] bg-opacity-20 group-hover:bg-[#add015] transition-colors">
                <FileText className="w-8 h-8 text-[#add015] group-hover:text-white" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#add015] transition-colors">
              Dokumentenablage
            </h3>
            <p className="text-gray-600">
              Fahrzeugdokumente und Unterlagen zentral verwalten
            </p>
          </Link>

          {/* Fahrer & Nutzer */}
          <Link
            to="/fahrer"
            className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-200 hover:border-[#add015]"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-[#add015] bg-opacity-20 group-hover:bg-[#add015] transition-colors">
                <Users className="w-8 h-8 text-[#add015] group-hover:text-white" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#add015] transition-colors">
              Fahrer & Nutzer
            </h3>
            <p className="text-gray-600">
              Fahrerprofile und Nutzerverwaltung mit Führerscheinkontrolle
            </p>
          </Link>

          {/* Car Police */}
          <Link
            to="/carpolice"
            className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-200 hover:border-[#add015]"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-[#add015] bg-opacity-20 group-hover:bg-[#add015] transition-colors">
                <Car className="w-8 h-8 text-[#add015] group-hover:text-white" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#add015] transition-colors">
              Car Police
            </h3>
            <p className="text-gray-600">
              Fahrzeugüberprüfung und Compliance-Kontrolle
            </p>
          </Link>

          {/* Fahrzeugkonfiguration */}
          <Link
            to="/fahrzeugkonfiguration"
            className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-200 hover:border-[#add015]"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-[#add015] bg-opacity-20 group-hover:bg-[#add015] transition-colors">
                <Settings className="w-8 h-8 text-[#add015] group-hover:text-white" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#add015] transition-colors">
              Fahrzeugkonfiguration
            </h3>
            <p className="text-gray-600">
              Fahrzeuge konfigurieren und Einstellungen anpassen
            </p>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default Hub; 